import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { BookOpen, ExternalLink, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSite } from "../lib/site";
import { Button } from "./ui/button";

export const downloadBinder = async () => {
  const id = toast.loading("Building the SDS binder - this can take a minute...");
  try {
    const r = await api.get("/sds/binder", { responseType: "blob", timeout: 180000 });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "SDS_Binder.pdf";
    a.click();
    toast.success("SDS binder downloaded", { id });
  } catch (e) { toast.error("Could not build the binder", { id }); }
};

const DemoPack = () => {
  const [st, setSt] = useState(null);
  const poll = () => api.get("/admin/demo-pack").then((r) => { setSt(r.data); if (r.data.status === "running") setTimeout(poll, 4000); });
  useEffect(() => { poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    if (!window.confirm("Load the demo pack? It adds sample safety data sheets, COSHH assessments, your WRM sheets for Paper Factory and example training records. Products already on a site are left as they are.")) return;
    try { setSt((await api.post("/admin/demo-pack")).data); setTimeout(poll, 4000); } catch (e) { toast.error(fmtErr(e)); }
  };
  const s = st?.stats;
  return (
    <div className="panel flex flex-wrap items-center gap-3 p-4 lg:col-span-2" data-testid="demo-pack">
      <div className="min-w-[240px] flex-1">
        <p className="font-semibold">Demo pack (admin)</p>
        <p className="text-xs text-slate-400" data-testid="demo-pack-status">
          {st?.status === "running" ? "Loading - this takes a few minutes..." : st?.status === "done" ? (Object.values(s).every((v) => !v) ? "Already loaded - nothing new to add." : `Loaded: ${s.chemicals_added} products added, ${s.sds_filled} SDS filled, ${s.coshh_added} COSHH assessments, ${s.completions} training records.`)
            : st?.status === "failed" ? `Failed: ${st.error}` : "Adds sample SDS and COSHH assessments to every site, your WRM sheets to Paper Factory and example training records."}
        </p>
      </div>
      <Button variant="outline" className="border-slate-700 bg-transparent" disabled={st?.status === "running"} onClick={start} data-testid="demo-pack-btn">{st?.status === "running" ? "Loading..." : "Load demo pack"}</Button>
    </div>
  );
};

export const SdsStation = () => {
  const { isApprover, user } = useAuth();
  const { site } = useSite();
  const [st, setSt] = useState(null);
  useEffect(() => { if (site) api.get("/sds/station").then((r) => setSt(r.data)); }, [site]);
  const url = st ? `${window.location.origin}/sds/${st.token}` : "";
  const rotate = async () => {
    if (!window.confirm("Create a new link? Existing printed posters will stop working and must be reprinted.")) return;
    try { const r = await api.post("/sds/station/rotate"); setSt({ ...st, token: r.data.token }); toast.success("New link created - reprint the poster"); } catch (e) { toast.error(fmtErr(e)); }
  };
  const print = () => {
    const svg = document.querySelector('[data-testid="sds-poster"] svg').outerHTML;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>SDS poster</title></head><body style="font-family:Arial,sans-serif;text-align:center;margin:0;padding:40px;border:14px solid #D4333B;min-height:90vh;box-sizing:border-box">
      <p style="color:#D4333B;font-weight:700;letter-spacing:3px;text-transform:uppercase">EP Group &middot; ${st.site}</p>
      <h1 style="font-size:54px;margin:10px 0">SAFETY DATA SHEETS</h1><p style="font-size:24px;font-weight:700">Scan with your phone camera for first aid, spill steps and the full SDS</p>
      <div style="margin:40px auto;width:420px">${svg.replace(/width="220" height="220"/, 'width="420" height="420"')}</div>
      <p style="background:#D4333B;color:#fff;font-size:44px;font-weight:900;padding:16px">EMERGENCY: CALL 999</p>
      <p style="font-size:18px">No login needed. A printed SDS binder is kept at this point.</p></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };
  if (!st) return <p className="text-sm text-slate-500">Loading...</p>;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]" data-testid="sds-station">
      <div className="panel space-y-3 p-5">
        <h3 className="font-display text-xl font-bold uppercase">Emergency SDS access - no login needed</h3>
        <p className="text-sm text-slate-300">Print this poster and fix it at each chemical store, the battery bay and the first-aid point. Anyone who scans it with a phone camera sees every product on this site, its first-aid and spill steps, and can open the full safety data sheet - even without an account.</p>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-300">
          <li>Print the poster (right) on A4 and laminate it.</li>
          <li>Keep the printed SDS binder next to it as a back-up for power or signal loss.</li>
          <li>Add or update products in the Inventory tab - the QR page updates instantly.</li>
        </ol>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={print} className="bg-rose-600 hover:bg-rose-700" data-testid="sds-print-poster"><Printer className="mr-1 h-4 w-4" />Print poster</Button>
          <Button variant="outline" className="border-slate-700 bg-transparent" onClick={downloadBinder} data-testid="sds-binder-btn"><BookOpen className="mr-1 h-4 w-4" />Download SDS binder (PDF)</Button>
          <a href={url} target="_blank" rel="noreferrer"><Button variant="outline" className="border-slate-700 bg-transparent" data-testid="sds-open-public"><ExternalLink className="mr-1 h-4 w-4" />Open worker view</Button></a>
          {isApprover && <Button variant="ghost" onClick={rotate} data-testid="sds-rotate"><RefreshCw className="mr-1 h-4 w-4" />New link</Button>}
        </div>
        <p className="break-all font-mono text-xs text-slate-500" data-testid="sds-public-url">{url}</p>
      </div>
      <div className="rounded-xl border-4 bg-white p-6 text-center text-black" style={{ borderColor: "#D4333B" }} data-testid="sds-poster">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "#D4333B" }}>EP Group · {st.site}</p>
        <p className="mt-1 text-2xl font-black uppercase leading-tight">Safety Data Sheets</p>
        <p className="text-sm font-semibold">Scan for first aid, spill steps &amp; full SDS</p>
        <div className="my-4 flex justify-center"><QRCodeSVG value={url} size={220} level="M" /></div>
        <p className="rounded py-2 text-lg font-black text-white" style={{ background: "#D4333B" }}>EMERGENCY: CALL 999</p>
        <p className="mt-2 text-[11px] text-black/60">No login needed · printed binder kept at this point</p>
      </div>
      {user?.role === "admin" && <DemoPack />}
    </div>
  );
};
